import React, { useEffect, useState, ChangeEvent } from "react";
import {
  styled,
  Box,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Button,
  SelectChangeEvent,
  StepButton,
  Dialog,
  CircularProgress,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DashboardFrame from "../../component/dashboard-frame";
import { IWithClassName } from "../../theme/default.theme";
import CampaignSettings from "./steps/campaign-settings";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";
import { IRootState, useAppDispatch, useAppSelector } from "../../redux/store";
import {
  getCampaignDetailsThunk,
  initCampaignThunk,
  saveCampaignThunk,
} from "../../redux/thunks/campaign.thunk";
import { CampaignFormSteps, BidTypes, Products } from "../../enum/campaign";
import Tracking from "./steps/tracking";
import AdCreatives from "./steps/ad-creatives";
import Summary from "./steps/summary";
import {
  RadioButtonUnchecked,
  RadioButtonChecked,
  CheckCircleRounded,
} from "@mui/icons-material";
import { IAppInfo } from "../../interfaces/dto/app.dto";
import { IList } from "../../interfaces/default";
import {
  ICampaignSettings,
  ICampaignSettingsError,
  IForm,
} from "../../interfaces/campaign";
import { Dayjs } from "dayjs";
import { CampaignType } from "../../enum/campaign";
import {
  campaignAdCreativesSchema,
  campaignSettingsSchema,
  campaignTrackingSchema,
} from "./validation";
import { useNavigate, useParams } from "react-router-dom";
import { createSelector } from "@reduxjs/toolkit";

const steps = [
  CampaignFormSteps.CampaignSettings,
  CampaignFormSteps.Tracking,
  CampaignFormSteps.AdCreatives,
  CampaignFormSteps.Summary,
];

const selector = createSelector(
  (state: IRootState) => state.campaign,
  (campaign) => ({
    campaignDetails: campaign.details.data,
    campaignSave: campaign.save,
    campaignUpdate: campaign.update,
  }),
);

const CreateCampaign = ({ className }: IWithClassName) => {
  const navigate = useNavigate();
  const { campaignDetails, campaignSave, campaignUpdate } =
    useAppSelector(selector);
  const { campaign, step } = useParams();

  const [userId, setUserId] = useState<string>();
  const [openDialog, setOpenDialog] = useState<{
    result: boolean;
    message: string;
    title: string;
  }>();
  const [settings, setSettings] = useState<ICampaignSettings>({
    product: Products.App,
    bidType: BidTypes.CPC,
    type: CampaignType.DirectApps,
  });

  const [campaignSettingsError, setCampaignSettingsError] =
    useState<ICampaignSettingsError>({});
  const dispatch = useAppDispatch();
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (campaign) {
      dispatch(getCampaignDetailsThunk(campaign));
    }

    if (step) {
      setCurrentStep(Number(step));
    }
  }, [dispatch, campaign, step]);

  useEffect(() => {
    if (campaignDetails) {
      setSettings({
        product: campaignDetails.product,
        bidType: campaignDetails.bidding?.bidType as BidTypes,
        type: campaignDetails.type,
        name: campaignDetails.name,
        startDate: campaignDetails.startDate,
        endDate: campaignDetails.endDate,
        webUrl: campaignDetails.webUrl,
        app: campaignDetails.app
          ? {
              id: campaignDetails.app.id,
              title: campaignDetails.app.name,
              url: campaignDetails.app.url,
              type: campaignDetails.app.type,
              thumbnail: campaignDetails.app.thumbnail,
              rating: "",
              author: "",
              link: campaignDetails.app.url,
              product_id: "",
              bundle_id: "",
            }
          : undefined,
        country: campaignDetails.target?.locations?.country,
        ageGroups: campaignDetails.target?.ageGroups,
        interests: campaignDetails.target?.interests,
        languages: campaignDetails.target?.languages,
        bidAmount: campaignDetails.bidding?.bidAmount,
        totalBudget: campaignDetails.bidding?.totalBudget,
        dailyBudget: campaignDetails.bidding?.dailyBudget,
        partner: campaignDetails.tracking?.partner,
        impressionUrl: campaignDetails.tracking?.impressionUrl,
        bannerImage: [],
        serverBanners: campaignDetails.creative,
      });
      const user = campaignDetails.userId ?? "";
      setUserId(typeof user === 'string' ? user : user._id);
    }
  }, [campaignDetails]);

  const datepickerHandler = (label: string, date: Dayjs | null) => {
    setSettings((state) => ({
      ...state,
      [label]: date?.format("YYYY-MM-DD"),
    }));
  };

  const changeAppHandler = (label: keyof IForm) => (value: IAppInfo) =>
    setSettings((state) => ({
      ...state,
      [label]: value,
    }));

  const multiSelectChangeHandler =
    (label: keyof IForm) => (value: Array<IList>) =>
      setSettings((state) => ({
        ...state,
        [label]: value,
      }));

  const changeTextHandler =
    (label: keyof IForm) =>
    ({ target }: ChangeEvent<HTMLInputElement>) =>
      setSettings((state) => ({
        ...state,
        [label]: target.value,
      }));

  const selectChangeHandler =
    (label: keyof IForm) => (value: SelectChangeEvent<string> | string) =>
      setSettings((state) => ({
        ...state,
        [label]: value,
      }));

  const changeFileHandler = (file: Array<File>) => {
    setSettings((state) => ({
      ...state,
      bannerImage: file,
    }));
  };

  useEffect(() => {
    dispatch(initCampaignThunk());
  }, [dispatch]);

  // useEffect(() => {
  //   if(details) {
  //     changeTextHandler("name")({target: {value: details.data?.name ?? ""}})
  //   }
  // }, [details]);

  const handleNext = async () => {
    setCampaignSettingsError({});

    switch (currentStep) {
      case 0:
        campaignSettingsSchema
          .validateAsync({
            name: settings.name,
            product: settings.product,
            type: settings.type,
            startDate: settings.startDate,
            endDate: settings.endDate,
            app: settings.app
              ? {
                  id: settings.app.id,
                  name: settings.app.title,
                  url: settings.app.url,
                  type: settings.app.type,
                }
              : {},
            webUrl: settings.webUrl,
            interests: settings.interests ?? [],
            ageGroups: settings.ageGroups ?? [],
            languages: settings.languages ?? [],
            country: settings.country,
            bidAmount: settings.bidAmount,
            totalBudget: settings.totalBudget,
            dailyBudget: settings.dailyBudget,
            bidType: settings.bidType,
          })
          .then(() => {
            setCurrentStep((prev) => prev + 1);
          })
          .catch((err) => {
            setCampaignSettingsError((state) => ({
              ...state,
              [err.details[0].path[0]]: err.details[0].message,
            }));
          });

        break;
      case 1:
        campaignTrackingSchema
          .validateAsync({
            partner: settings.partner,
            impressionUrl: settings.impressionUrl?.replaceAll("}", "").replaceAll("{", ""),
          })
          .then(() => {
            setCurrentStep((prev) => prev + 1);
          })
          .catch((err) => {
            setCampaignSettingsError((state) => ({
              ...state,
              [err.details[0].path[0]]: err.details[0].message,
            }));
          });

        break;
      case 2:
        if (!campaign) {
          campaignAdCreativesSchema
            .validateAsync({
              bannerImage: settings.bannerImage,
            })
            .then(() => {
              setCurrentStep((prev) => prev + 1);
            })
            .catch((err) => {
              console.log({ err: err.details });
              setCampaignSettingsError((state) => ({
                ...state,
                [err.details[0].path[0]]: err.details[0].message,
              }));
            });
        } else {
          setCurrentStep((prev) => prev + 1);
        }
        break;
      default:
        break;
    }
  };

  const submitCampaign = async () => {
    const payload = {
      name: settings.name ?? "",
      type: settings.type ?? "",
      startDate: settings.startDate ?? "",
      endDate: settings.endDate ?? "",
      webUrl:
        settings.product === Products.Website ? settings.webUrl : undefined,
      app:
        settings.product === Products.App
          ? {
              id: settings.app?.id ?? "",
              name: settings.app?.title ?? "",
              url: settings.app?.url ?? "",
              type: settings.app?.type ?? "",
              thumbnail: settings.app?.thumbnail ?? "",
            }
          : undefined,
      target: {
        interests: settings.interests ?? [],
        ageGroups: settings.ageGroups ?? [],
        languages: settings.languages ?? [],
        locations: {
          country: settings.country ?? "",
        },
      },
      bidding: {
        bidAmount: settings.bidAmount ?? 0,
        totalBudget: settings.totalBudget ?? 0,
        dailyBudget: settings.dailyBudget ?? 0,
        bidType: settings.bidType ?? "",
      },
      tracking: {
        partner: settings.partner ?? "",
        impressionUrl: settings.impressionUrl ?? "",
      },
      product: settings.product,
    };

    const result = await dispatch(
      saveCampaignThunk(payload, campaign, settings.bannerImage, userId),
    );

    setOpenDialog({
      result: result,
      message: result
        ? `Campaign ${campaign ? "updated" : "added"} successfully!!`
        : "Some Error Occured!!",
      title: result ? "Success" : "Error",
    });
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const processStepIconHandler = (step: number) => {
    if (step === currentStep) {
      return <RadioButtonChecked color="primary" />;
    } else if (step < currentStep) {
      return <CheckCircleRounded color="success" />;
    } else {
      return <RadioButtonUnchecked color="disabled" />;
    }
  };

  const handleStepClick = (step: number) => () => {
    if (step <= currentStep) {
      setCurrentStep(step);
    }
  };

  const okClick = () => {
    setOpenDialog(undefined);
    if (openDialog?.result) {
      navigate("/");
    }
  };

  return (
    <DashboardFrame title="Campaign">
      <Box className={className}>
        <Box component={Paper} className="stepper-container">
          <Stepper activeStep={currentStep} alternativeLabel variant="outlined">
            {steps.map((label, index) => (
              <Step key={label} index={index}>
                <StepButton color="inherit" onClick={handleStepClick(index)}>
                  <StepLabel
                    StepIconProps={{
                      icon: processStepIconHandler(index),
                    }}
                  >
                    {label}
                  </StepLabel>
                </StepButton>
              </Step>
            ))}
          </Stepper>
        </Box>

        <CampaignSettings
          activeStep={steps[currentStep]}
          form={settings}
          datepickerHandler={datepickerHandler}
          changeAppHandler={changeAppHandler}
          multiSelectChangeHandler={multiSelectChangeHandler}
          changeTextHandler={changeTextHandler}
          selectChangeHandler={selectChangeHandler}
          error={campaignSettingsError}
          setUserId={setUserId}
          userId={userId}
          isEditMode={Boolean(campaign)}
        />

        <Tracking
          activeStep={steps[currentStep]}
          form={{
            partner: settings.partner,
            impressionUrl: settings.impressionUrl,
          }}
          error={{
            partner: campaignSettingsError.partner,
            impressionUrl: campaignSettingsError.impressionUrl,
          }}
          changeTextHandler={changeTextHandler}
        />

        <AdCreatives
          activeStep={steps[currentStep]}
          form={settings}
          error={campaignSettingsError}
          changeFileHandler={changeFileHandler}
        />
        <Summary activeStep={steps[currentStep]} form={settings} />

        <Box component={Paper} className="action-cont">
          <Button
            variant="contained"
            color="primary"
            startIcon={<ArrowBackIos />}
            onClick={handleBack}
            disabled={currentStep === 0}
          >
            Previous
          </Button>
          {currentStep === steps.length - 1 ? (
            campaignSave.isLoading || campaignUpdate.isLoading ? (
              <CircularProgress />
            ) : (
              <Button
                variant="contained"
                color="success"
                onClick={submitCampaign}
              >
                Submit
              </Button>
            )
          ) : (
            <Button
              variant="contained"
              color="primary"
              endIcon={<ArrowForwardIos />}
              onClick={handleNext}
            >
              Next
            </Button>
          )}
        </Box>
      </Box>
      <Dialog
        open={Boolean(openDialog)}
        onClose={() => setOpenDialog(undefined)}
      >
        <DialogTitle>{openDialog?.title}</DialogTitle>
        <DialogContent>{openDialog?.message}</DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={okClick}>
            Ok
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardFrame>
  );
};

export default styled(CreateCampaign)(({ theme }) => ({
  display: "grid",
  gridTemplaterows: "80px 1fr 50px",
  gap: theme.spacing(2),
  ".stepper-container": {
    padding: theme.spacing(2, 0),
  },
  ".action-cont": {
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(1, 10, 1, 2),
  },
  ".Mui-completed": {
    svg: {
      color: theme.palette.success.main,
    },
  },
}));
